import { useCallback } from 'react';
import { EventSearchForm } from '@mark43/cad-api';
import { useEnabledFeatures } from '../common/hooks/enabled-features';
import { useBulkExportEventSearchResultsMutation } from './use-bulk-export-event-search-results-mutation';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { useExportEventSearchResultsMutation } from './use-export-event-search-results-mutation';
import { FileExportHandlerType, FileExportType } from './types';
import { generateEnhancedSearchReport } from './utils';

type UseEventSearchFileExportReturn = {
    isExportLoading: boolean;
    isCSVExportLoading: boolean;
    isConsolidatedExportAllowed: boolean;
    fileExportHandler: FileExportHandlerType;
};

export function useEventSearchFileExport(
    formData: EventSearchForm
): UseEventSearchFileExportReturn {
    const { enhancedSearchExportsEnabled, consolidatedEventExportEnabled } = useEnabledFeatures();
    const bulkEventSearchExportMutation = useBulkExportEventSearchResultsMutation();
    const { open } = useFullscreenModalControls();
    const exportEventSearchResultsMutation = useExportEventSearchResultsMutation();

    const isConsolidatedExportAllowed =
        consolidatedEventExportEnabled && enhancedSearchExportsEnabled;
    const fileExportHandler = useCallback(
        ({ type, entityIds }: { type?: FileExportType; entityIds?: number[] }) => {
            const isAllSelected = !entityIds?.length;

            switch (type) {
                case FileExportType.PDF: {
                    if (enhancedSearchExportsEnabled) {
                        generateEnhancedSearchReport({
                            eventIds: entityIds ?? [],
                            open,
                            bulkEventSearchExportMutation,
                            ...(isAllSelected ? { eventSearchForm: formData } : {}),
                        });
                    }

                    break;
                }
                case FileExportType.CSV: {
                    if (enhancedSearchExportsEnabled) {
                        exportEventSearchResultsMutation.mutate({
                            consolidatedEventExport: false, // generates CSV
                            eventIds: entityIds ?? [],
                            eventSearchForm: isAllSelected ? formData : undefined,
                        });
                    }
                    break;
                }
                default: {
                    exportEventSearchResultsMutation.mutate({
                        consolidatedEventExport: false, // generates CSV , if 'true' generates XLSX
                        eventIds: entityIds ?? [],
                        eventSearchForm: isAllSelected ? formData : undefined,
                    });
                }
            }
        },
        [
            bulkEventSearchExportMutation,
            enhancedSearchExportsEnabled,
            exportEventSearchResultsMutation,
            open,
            formData,
        ]
    );

    return {
        isExportLoading: enhancedSearchExportsEnabled
            ? bulkEventSearchExportMutation.isLoading
            : exportEventSearchResultsMutation.isLoading,
        isCSVExportLoading:
            enhancedSearchExportsEnabled && exportEventSearchResultsMutation.isLoading,
        isConsolidatedExportAllowed,
        fileExportHandler,
    };
}
